import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  List,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';
import { QUICK_SEARCH_OPTIONS } from '../../constants/mainLayout';
import QuickSearchItem from './QuickSearchItem';

/**
 * SearchDialog Component
 * Search dialog with input and quick search options
 */
const SearchDialog = ({
  open,
  onClose,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  onQuickSearch,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: borderRadius.modal,
          boxShadow: shadows.xl,
          mt: 8,
          maxHeight: 'calc(100vh - 64px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: `${spacing.sm} ${spacing.md}`,
          paddingBottom: spacing.xs,
          borderBottom: `1px solid ${colors.borderLight}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: colors.backgroundWhite,
          minHeight: 'auto',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: fonts.weightBold,
            fontSize: fonts.fontSizeLg,
            color: colors.textPrimaryDark,
            lineHeight: 1.2,
          }}
        >
          Search
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: colors.textSecondary,
            padding: '4px',
            width: '28px',
            height: '28px',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: colors.backgroundLight,
              color: colors.textPrimaryDark,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          padding: spacing.md,
          paddingTop: spacing.sm,
          backgroundColor: colors.backgroundWhite,
          '&.MuiDialogContent-root': {
            paddingTop: spacing.sm,
          },
        }}
      >
        <TextField
          fullWidth
          autoFocus
          placeholder="Search queries, projects, or users..."
          value={searchQuery}
          onChange={onSearchChange}
          onKeyDown={onSearchSubmit}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textSecondary, fontSize: '18px' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: spacing.sm,
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.backgroundSoft,
              borderRadius: borderRadius.input,
              fontSize: fonts.fontSizeMd,
              height: '40px',
              '& fieldset': {
                borderColor: colors.borderDefault,
              },
              '&:hover fieldset': {
                borderColor: colors.borderHover,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary,
                borderWidth: '1.5px',
              },
            },
            '& .MuiInputBase-input': {
              padding: '10px 14px',
              fontSize: fonts.fontSizeMd,
            },
          }}
        />
        
        {/* Quick Search Options */}
        <Box sx={{ mt: spacing.sm }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: fonts.weightSemiBold,
              fontSize: '11px',
              color: colors.textSecondary,
              mb: spacing.xs,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: 1.2,
            }}
          >
            Quick Search
          </Typography>
          <List sx={{ p: 0, mt: '2px' }}>
            {QUICK_SEARCH_OPTIONS.map((option, index) => (
              <QuickSearchItem
                key={option.route}
                primary={option.primary}
                secondary={option.secondary}
                onClick={() => onQuickSearch(option.route)}
                isLast={index === QUICK_SEARCH_OPTIONS.length - 1}
              />
            ))}
          </List>
        </Box>

        {searchQuery.trim() && (
          <Box 
            sx={{ 
              mt: spacing.sm,
              pt: spacing.xs,
              borderTop: `1px solid ${colors.borderLight}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: fonts.fontSizeXs,
                color: colors.textSecondary,
                fontStyle: 'italic',
                lineHeight: 1.3,
              }}
            >
              Press <Box component="span" sx={{ fontWeight: fonts.weightMedium, color: colors.textPrimaryDark }}>Enter</Box> to search for "{searchQuery}"
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
